import React, { useCallback, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

// region Helpers
import utils from "@helpers/utils";
import gqlUtils from "@store/graphql/helpers/utils.graphql";
// endregion Helpers
// region Libraries
import Yup from "@libraries/yup";
import jwt from "jwt-decode";
import { IconButton, TextField } from "@libraries/mui";
import { Visibility, VisibilityOff } from "@libraries/icons";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Libraries
// region Graphql
import { useMutation } from "@apollo/client";
import { TUserUpdateMutations, userUpdate } from "@store/graphql/groups/user/mutation.graphql";
import { ECommandResultStatus, UserMutationsUpdateArgs } from "@store/graphql/helpers/types.graphql";
// endregion Graphql
// region Hooks
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "@hooks/useAuth";
// endregion Hooks
// region Atoms
import ExternalSvg from "@atoms/ExternalSvg";
// endregion Atoms
// region Language
import useTranslation from "@languages/useTranslation";
import { LoginMessages } from "@languages/interfaces/loginMessages";
// endregion Language
// region Styles

import * as Styled from "./styles";

// endregion Styles

interface ITokenData {
  exp: number;
  iat: number;
  nbf: number;
  primarysid: string;
  email: string;
  name: string;
  admin: string;
  claims: { [key: string]: string[] };
  roles: { [key: string]: { [key: string]: string[] } };
  modules: { [key: string]: string[] };
}

interface ISignInFormData {
  email: string;
  password: string;
}

const LoginForm: React.FC = () => {

  const navigate = useNavigate();
  const {
    signIn
  } = useAuth();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [UpdateUserValidation] = useMutation<TUserUpdateMutations, UserMutationsUpdateArgs>(userUpdate(["id", "fullName", "isValidated"]));

  // Define the validation types
  const loginSchema = Yup.object()
    .shape({
      email: Yup.string()
        .trim()
        .required(t(LoginMessages.loginRequiredEmail))
        .email(t(LoginMessages.loginInvalidEmail)),
      password: Yup.string()
        .trim()
        .required(t(LoginMessages.loginPasswordRequired))
    });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<ISignInFormData>({
    resolver: yupResolver(loginSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  /**
   * Login action
   * @param email Email
   * @param password Password
   */
  const logIn = useCallback(
    async (email: string, password: string) => {
      try {

        setIsLoading(true);

        // Request login access
        const response = await signIn({
          email,
          password
        });
        const statusToast = utils.getToastStatusType(response.status);

        addToast({
          type: statusToast,
          title: response.message
        });

        if (gqlUtils.commandStatusToString(response.status) === ECommandResultStatus.SUCCESS) {
          const userTokenData: ITokenData = jwt(response.result!.token!);

          await UpdateUserValidation({
            variables: {
              user: {
                id: userTokenData.primarysid,
                isValidated: true
              }
            }
          });

        }

        navigate("");

      } catch (error) {
        addToast({
          type: "info",
          title: t(LoginMessages.loginFailed)
        });
      } finally {
        setIsLoading(false);
      }
    },
    [signIn, addToast, navigate, t]
  );

  // Handle form controllers
  const handleClickShowPassword = () => setShowPassword((state) => !state);
  const handleSubmitForm: SubmitHandler<ISignInFormData> = (
    formData: ISignInFormData
  ) => logIn(formData.email, formData.password);

  return (
    <Styled.LoginFormContainer>
      <Styled.LoginFormLogo>
        <ExternalSvg svgName="Logotipo" className="logoDiv" />
      </Styled.LoginFormLogo>
      <form onSubmit={handleSubmit(handleSubmitForm)} style={{ margin: 20 }}>
        <Controller // Email
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="username"
              label={t(LoginMessages.loginEmailLabel)}
              type="email"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.email}
              helperText={formErrors.email && formErrors.email.message}
              className="default-field"
            />
          )}
        />
        <Controller // Password
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="current-password"
              label={t(LoginMessages.loginPasswordLabel)}
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.password}
              helperText={formErrors.password && formErrors.password.message}
              className="default-field"
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />
          )}
        />
        <Button
          variant="contained"
          size="large"
          type="submit"
          text={t(LoginMessages.loginLoginButton)}
          loading={isLoading}
        />
      </form>
      <Styled.LoginCallToAction>
        <Link to="/sign-up">Não sou cadastrado</Link>
        <Link to="/forgot-password">Esqueci minha senha</Link>
        <Link to="/resend-email-confirmation">Reenviar e-mail de confirmação</Link>
      </Styled.LoginCallToAction>
    </Styled.LoginFormContainer>
  );
};

export default LoginForm;
